import React, { useEffect, useState, useMemo } from 'react'
import { Users, Building2, Shield, CheckCircle2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import AdminSidebar from '@/features/admin/components/AdminSidebar'
import * as adminApi from '@/features/admin/services/adminApi'

interface StatCard {
  label: string
  value: number | null
  icon: React.ReactNode
  loading: boolean
}

const AdminDashboardPage: React.FC = () => {
  const { currentTheme } = useTheme()

  const [totalUsers, setTotalUsers] = useState<number | null>(null)
  const [totalWorkspaces, setTotalWorkspaces] = useState<number | null>(null)
  const [superAdmins, setSuperAdmins] = useState<number | null>(null)
  const [activeWorkspaces, setActiveWorkspaces] = useState<number | null>(null)
  const [usersLoading, setUsersLoading] = useState(true)
  const [workspacesLoading, setWorkspacesLoading] = useState(true)

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // page=1, limit=100 to count super_admins on first page — for totals we use the response.total
        const res = await adminApi.listUsers(1, 100)
        setTotalUsers(res.total)
        const admins = res.data.filter((u) => u.platformRole === 'super_admin').length
        setSuperAdmins(admins)
      } catch {
        setTotalUsers(0)
        setSuperAdmins(0)
      } finally {
        setUsersLoading(false)
      }
    }

    const fetchWorkspaces = async () => {
      try {
        const res = await adminApi.listWorkspaces(1, 100)
        setTotalWorkspaces(res.total)
        const active = res.data.filter((w) => w.isActive).length
        setActiveWorkspaces(active)
      } catch {
        setTotalWorkspaces(0)
        setActiveWorkspaces(0)
      } finally {
        setWorkspacesLoading(false)
      }
    }

    void fetchUsers()
    void fetchWorkspaces()
  }, [])

  const stats: StatCard[] = useMemo(
    () => [
      {
        label: 'Total Users',
        value: totalUsers,
        icon: <Users size={22} />,
        loading: usersLoading,
      },
      {
        label: 'Total Workspaces',
        value: totalWorkspaces,
        icon: <Building2 size={22} />,
        loading: workspacesLoading,
      },
      {
        label: 'Super Admins',
        value: superAdmins,
        icon: <Shield size={22} />,
        loading: usersLoading,
      },
      {
        label: 'Active Workspaces',
        value: activeWorkspaces,
        icon: <CheckCircle2 size={22} />,
        loading: workspacesLoading,
      },
    ],
    [totalUsers, totalWorkspaces, superAdmins, activeWorkspaces, usersLoading, workspacesLoading],
  )

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <AdminSidebar />

      <main className="lg:pl-56 min-h-screen">
        <div className="px-4 pt-16 pb-8 sm:px-6 lg:px-8 lg:pt-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold" style={{ color: currentTheme.text }}>
              Dashboard
            </h1>
            <p className="mt-1 text-sm" style={{ color: currentTheme.secondrytext }}>
              Platform overview
            </p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border p-5 flex flex-col gap-3"
                style={{
                  backgroundColor: currentTheme.secondary,
                  borderColor: currentTheme.border,
                  boxShadow: currentTheme.boxShadow,
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                  style={{
                    backgroundColor: `${currentTheme.accent}18`,
                    color: currentTheme.accent,
                  }}
                >
                  {stat.icon}
                </div>
                {stat.loading ? (
                  <div
                    className="h-8 w-16 rounded-lg animate-pulse"
                    style={{ backgroundColor: currentTheme.border }}
                  />
                ) : (
                  <p
                    className="text-3xl font-bold tabular-nums"
                    style={{ color: currentTheme.text }}
                  >
                    {stat.value ?? 0}
                  </p>
                )}
                <p className="text-sm font-medium" style={{ color: currentTheme.secondrytext }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminDashboardPage
